import { useCallback, useMemo, useState } from "react";
import type { TeamLiveGridTile } from "@/components/liveStream/TeamLiveSortableGrid";
import {
  deviceTileKey,
  externalTileKey,
  loadTeamLiveTileOrder,
  memberTileKey,
  mergeTeamLiveTileOrder,
  moveTileKey,
  saveTeamLiveTileOrder,
} from "@/utils/teamLiveTileOrder";
import { TEAM_LIVE_MAX_TILES } from "@/utils/teamLiveGrid";
import type { TeamLiveDeviceEntry, TeamLiveStreamEntry } from "@/types";
import type { TeamExternalStream } from "@/utils/teamLiveExternalStreams";
import { formatUserName } from "@/utils/userName";

export interface UseTeamLiveTilesResult {
  /** Ordered, capped tiles for the multiview grid. */
  tiles: TeamLiveGridTile[];
  /** Apply + persist a new order (tile keys) after a completed drag. */
  handleOrderChange: (orderedKeys: string[]) => void;
  /** Swap a tile with its neighbour (mobile edit mode); persists the order. */
  handleMoveTile: (key: string, direction: -1 | 1) => void;
}

/**
 * Viewer-local tile ordering for the Team Live multiview: stored order
 * first (localStorage, per team), unseen tiles appended in server order
 * (members by createdAt desc, then online devices, then externals). The
 * viewer's order decides which tiles are visible at the cap — devices sit
 * behind streams, so at the cap a live car always beats an idle phone. See
 * docs/superpowers/specs/2026-08-24-team-live-tile-reorder-design.md
 */
export function useTeamLiveTiles(
  teamId: string,
  streamingEntries: TeamLiveStreamEntry[],
  deviceEntries: TeamLiveDeviceEntry[],
  externalStreams: TeamExternalStream[],
): UseTeamLiveTilesResult {
  const [tileOrder, setTileOrder] = useState<string[]>(() =>
    teamId ? loadTeamLiveTileOrder(teamId) : [],
  );

  const currentTiles: TeamLiveGridTile[] = useMemo(() => {
    const current: TeamLiveGridTile[] = [
      ...streamingEntries.map((entry) => ({
        kind: "member" as const,
        key: memberTileKey(entry.liveStream.id),
        entry,
        handleLabel:
          formatUserName(entry.member) ||
          entry.session?.title ||
          entry.liveStream.id,
      })),
      ...deviceEntries.map((entry) => ({
        kind: "device" as const,
        key: deviceTileKey(entry.device.id),
        entry,
        handleLabel:
          formatUserName(entry.member) ||
          entry.device.name ||
          entry.device.id,
      })),
      ...externalStreams.map((stream) => ({
        kind: "external" as const,
        key: externalTileKey(stream.id),
        stream,
        handleLabel: stream.label ?? stream.videoId,
      })),
    ];
    return current;
  }, [streamingEntries, deviceEntries, externalStreams]);

  /** Merged (uncapped) key order: stored first, then unseen in server order. */
  const fullOrder = useMemo(
    () => mergeTeamLiveTileOrder(tileOrder, currentTiles.map((t) => t.key)),
    [tileOrder, currentTiles],
  );

  const tiles: TeamLiveGridTile[] = useMemo(
    () =>
      fullOrder
        .map((key) => currentTiles.find((tile) => tile.key === key))
        .filter((tile): tile is TeamLiveGridTile => !!tile)
        .slice(0, TEAM_LIVE_MAX_TILES),
    [fullOrder, currentTiles],
  );

  const handleOrderChange = useCallback(
    (orderedKeys: string[]) => {
      setTileOrder(orderedKeys);
      saveTeamLiveTileOrder(teamId, orderedKeys);
    },
    [teamId],
  );

  const handleMoveTile = useCallback(
    (key: string, direction: -1 | 1) => {
      const nextKeys = moveTileKey(fullOrder, key, direction);
      if (nextKeys === fullOrder) return;
      setTileOrder(nextKeys);
      saveTeamLiveTileOrder(teamId, nextKeys);
    },
    [fullOrder, teamId],
  );

  return { tiles, handleOrderChange, handleMoveTile };
}
